/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { KeycloakResourceService } from '@administration/keycloak-shared';
import { createEmptyStateResource, createStateResource, StateResource } from '@common/tech-shared';
import { inject, Injectable } from '@angular/core';
import { map, Observable, startWith, tap } from 'rxjs';
import { ClientRoles, User } from './user.model';
import { UserRepository } from './user.repository';
import { UserUtil } from './user.util';

@Injectable({
  providedIn: 'root',
})
export class UserService extends KeycloakResourceService<User> {
  private readonly userRepository = inject(UserRepository);

  _getItemsFromKeycloak(): Observable<User[]> {
    return this.userRepository.getUsers().pipe(map(UserUtil.filterTechnicalUsers), map(UserUtil.sortUsersByLastName));
  }

  _createInKeycloak(user: User): Observable<User> {
    return this.userRepository
      .createInKeycloak(user)
      .pipe(tap((createdUser: User): void => this.userRepository._sendActivationMail(createdUser.id)));
  }

  _saveInKeycloak(user: User): Observable<User> {
    return this.userRepository.saveInKeycloak(user);
  }

  _deleteInKeycloak(id: string): Observable<void> {
    return this.userRepository.deleteUser(id);
  }

  public getUserById(userId: string): Observable<StateResource<User>> {
    return this.userRepository.getUserById(userId).pipe(startWith(createEmptyStateResource<User>(true)));
  }

  public getAvailableRoles(): Observable<StateResource<ClientRoles>> {
    return this.userRepository.getAvailableClientRoles().pipe(
      map((clientRoles: ClientRoles): StateResource<ClientRoles> => createStateResource(clientRoles)),
      startWith(createEmptyStateResource<ClientRoles>(true)),
    );
  }
}
